/**
 * Sign in, sign up, and password reset — task P3-06.
 *
 * One component with a mode, rather than three near-identical forms. The three
 * screens differ by two fields and a button label; splitting them produces
 * three places to fix the next time the error handling changes.
 *
 * Two behaviours worth keeping:
 *
 *  - The "check your inbox" state after signup and after a reset request looks
 *    the SAME whether or not the address has an account. The server is careful
 *    not to leak that (design §7.2); a UI that said "no such account" would
 *    hand it straight back.
 *
 *  - Errors are read off the RESOLVED value, not from a catch. Better Auth
 *    reports failures as `{ error }` rather than by rejecting, so a form that
 *    only catches rejections quietly treats a wrong password as success.
 */
import { useState, type FormEvent } from "react";

import { authClient, authErrorMessage } from "../auth.js";

export type AuthMode = "sign-in" | "sign-up" | "forgot";

const TITLES: Record<AuthMode, string> = {
    "sign-in": "Sign in",
    "sign-up": "Create an account",
    forgot: "Reset your password",
};

export function AuthPage({ mode, onModeChange }: {
    mode: AuthMode;
    onModeChange: (mode: AuthMode) => void;
}) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [name, setName] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [sent, setSent] = useState<string | null>(null);

    async function submit(event: FormEvent) {
        event.preventDefault();
        setBusy(true);
        setError(null);

        try {
            if (mode === "sign-up") {
                const result = await authClient.signUp.email({
                    email,
                    password,
                    name: name || email,
                });
                const message = authErrorMessage(result);
                if (message) {
                    setError(message);
                } else {
                    setSent(
                        "Check your inbox for a link to confirm your email address.",
                    );
                }
            } else if (mode === "forgot") {
                const result = await authClient.requestPasswordReset({
                    email,
                    redirectTo: `${window.location.origin}/#/reset-password`,
                });
                const message = authErrorMessage(result);
                // Deliberately NOT surfacing "no such account": the server
                // answers identically either way, and saying otherwise here
                // would undo that.
                if (message && message.includes("Too many")) {
                    setError(message);
                } else {
                    setSent(
                        "If that address has an account, a reset link is on its way.",
                    );
                }
            } else {
                const result = await authClient.signIn.email({ email, password });
                const message = authErrorMessage(result);
                if (message) setError(message);
                // On success the session hook re-renders the app; nothing to do.
            }
        } catch (caught) {
            setError(String(caught));
        } finally {
            setBusy(false);
        }
    }

    if (sent) {
        return (
            <div className="auth-card">
                <h1>{TITLES[mode]}</h1>
                <p className="note">{sent}</p>
                <button type="button" onClick={() => { setSent(null); onModeChange("sign-in"); }}>
                    Back to sign in
                </button>
            </div>
        );
    }

    return (
        <form className="auth-card" onSubmit={submit}>
            <h1>{TITLES[mode]}</h1>

            {mode === "sign-up" ? (
                <label>
                    <span>Name</span>
                    <input
                        value={name}
                        autoComplete="name"
                        onChange={(event) => setName(event.target.value)}
                    />
                </label>
            ) : null}

            <label>
                <span>Email</span>
                <input
                    type="email"
                    required
                    value={email}
                    autoComplete="email"
                    onChange={(event) => setEmail(event.target.value)}
                />
            </label>

            {mode === "forgot" ? null : (
                <label>
                    <span>Password</span>
                    <input
                        type="password"
                        required
                        // The server requires 12; saying so up front beats a
                        // round trip that rejects what was just typed.
                        minLength={mode === "sign-up" ? 12 : undefined}
                        value={password}
                        autoComplete={
                            mode === "sign-up" ? "new-password" : "current-password"
                        }
                        onChange={(event) => setPassword(event.target.value)}
                    />
                    {mode === "sign-up" ? (
                        <small>At least 12 characters. Length beats punctuation.</small>
                    ) : null}
                </label>
            )}

            {error ? <p className="error">{error}</p> : null}

            <button type="submit" disabled={busy}>
                {busy ? "Working…" : TITLES[mode]}
            </button>

            <div className="auth-links">
                {mode !== "sign-in" ? (
                    <button type="button" onClick={() => onModeChange("sign-in")}>
                        Sign in
                    </button>
                ) : null}
                {mode !== "sign-up" ? (
                    <button type="button" onClick={() => onModeChange("sign-up")}>
                        Create an account
                    </button>
                ) : null}
                {mode !== "forgot" ? (
                    <button type="button" onClick={() => onModeChange("forgot")}>
                        Forgot password
                    </button>
                ) : null}
            </div>
        </form>
    );
}

/**
 * Lands the user after they click the link in a reset email.
 *
 * The token comes from the query string. Better Auth does not sign the user in
 * on reset, so the form hands off to sign-in afterwards.
 */
export function ResetPasswordPage({ token, onDone }: {
    token: string;
    onDone: () => void;
}) {
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    async function submit(event: FormEvent) {
        event.preventDefault();
        setBusy(true);
        setError(null);

        const result = await authClient.resetPassword({ newPassword: password, token });
        const message = authErrorMessage(result);
        setBusy(false);

        if (message) setError(message);
        else onDone();
    }

    return (
        <form className="auth-card" onSubmit={submit}>
            <h1>Choose a new password</h1>
            <label>
                <span>New password</span>
                <input
                    type="password"
                    required
                    minLength={12}
                    autoComplete="new-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                />
                <small>At least 12 characters.</small>
            </label>
            {error ? <p className="error">{error}</p> : null}
            <button type="submit" disabled={busy}>
                {busy ? "Working…" : "Set password"}
            </button>
        </form>
    );
}
